import { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate, type HttpError } from "@refinedev/core";
import { Field, TextareaField, type Column } from "@fawkes/ui";

import { commEditorExtensions, normalizeVariables } from "./commEditorExtensions";
import { EditorToolbar } from "./EditorToolbar";
import { VariablePalette, type AiUitkomst } from "./VariablePalette";
import { CommAiPanel } from "./CommAiPanel";
import { DocumentImportDialog } from "./DocumentImportDialog";
import { importTaak, useImportTaak } from "./importTaak";
import { TestMailDialog } from "./TestMailDialog";
import { MediaLibraryPicker } from "./MediaLibraryPicker";
import { fetchPrintCss } from "../commMediaApi";
import { isRawPrintBody } from "./rawPrint";
import { RawPrintCanvas, type RawPrintHandle } from "./RawPrintCanvas";
import { CommAiSelectieDialoog } from "./CommAiSelectieDialoog";
import { CommEditorShell, type EditorPaneel } from "./CommEditorShell";
import { RawPrintToolbar } from "./RawPrintToolbar";
import { CommAiStrip } from "./CommAiStrip";
import { CeIcon } from "./CeIcon";
import { categoryLabel, originLabel, statusLabel, typeLabel } from "../commLabels";
import { formatDateTime } from "../../../i18n/format";
import type { CommTemplateDetailData, CommTemplateVersion } from "../commTypes";
import { IndeelbareTabel } from "../../../components/IndeelbareTabel";

// Mirrors the Jmix computeAiEnabled() permission (`communicatieAi.use` → ROLE_COMMUNICATIE_AI): the AI
// panel is hidden entirely for principals without it.
const AI_ROLE = "ROLE_COMMUNICATIE_AI";
// Editor-only affordances (test-mail, insert-from-library) gate on the edit authority.
const EDITOR_ROLE = "ROLE_COMMUNICATIE_EDITOR";

// communicatie (Slice 6): the editable WYSIWYG template editor (MVP). Loads the template bodyHtml into a
// TipTap document (bare {tokens} normalized to pills), exposes a formatting toolbar + a variable palette,
// and saves/publishes/restores through the Slice-5 write API. All URLs are RELATIVE (the dataProvider
// prepends /api once). DEFERRED (no backend yet, no dead buttons shown): the AI panel, test-mail send,
// and insert-from-media-library. Opening a template always lands here (there is no read-only detail in
// between); the preview and the version history are panes of this editor.

// Carried on the HttpError by the dataProvider for a structured {message, errors[]} body (422/409).
type CommHttpError = HttpError & { validationErrors?: string[] };

// Letterhead (briefhoofd/huisstijl) — faithful port of the Jmix shape stored in COMM_TEMPLATE.LETTERHEAD_JSON:
// {brand, name, lines[], footer} (see Jmix comm-editor.js _collectLetterhead + CommunicationDetailView).
// The address block is edited as multi-line text (one line per textarea row), mirroring the contenteditable
// `.ce-lh-addr` (joined on <br>). An empty letterhead is sent as null so the server keeps falling back to the
// vestiging-derived default (Jmix applyLetterheadState only stores an override when it diverges).
interface Letterhead {
  brand: string;
  name: string;
  lines: string[];
  footer: string;
}

function parseLetterhead(json: string | null): Letterhead {
  if (!json) return { brand: "", name: "", lines: [], footer: "" };
  try {
    const raw = JSON.parse(json) as Partial<Letterhead> & { lines?: unknown };
    const lines = Array.isArray(raw.lines)
      ? raw.lines.map((l) => String(l ?? "")).filter((l) => l.trim().length > 0)
      : [];
    return {
      brand: typeof raw.brand === "string" ? raw.brand : "",
      name: typeof raw.name === "string" ? raw.name : "",
      lines,
      footer: typeof raw.footer === "string" ? raw.footer : "",
    };
  } catch {
    return { brand: "", name: "", lines: [], footer: "" };
  }
}

// Serialize the editor state back to the {brand,name,lines[],footer} JSON the api/ persists. When every field
// is blank we return null so the template falls back to the default letterhead (matches Jmix's "store only an
// override" behavior — an all-empty letterhead is indistinguishable from "no override").
function serializeLetterhead(lh: Letterhead): string | null {
  const brand = lh.brand.trim();
  const name = lh.name.trim();
  const footer = lh.footer.trim();
  const lines = lh.lines.map((l) => l.trim()).filter((l) => l.length > 0);
  if (!brand && !name && !footer && lines.length === 0) return null;
  return JSON.stringify({ brand, name, lines, footer });
}

interface SaveValues {
  subject: string | null;
  bodyHtml: string;
  letterheadJson: string | null;
  version: number | null;
}

export function CommTemplateEditor({
  template,
  onSaved,
  onCancel,
  onChanged,
}: {
  template: CommTemplateDetailData;
  /** Called with the fresh server detail after a successful save/publish/restore (carries the new version). */
  onSaved: (fresh: CommTemplateDetailData) => void;
  onCancel: () => void;
  /** Notifies the host that there are unsaved edits (to guard tab-switch / back). */
  onChanged?: (dirty: boolean) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { mutate, isLoading: saving } = useCustomMutation();
  // Als belofte en niet als terugroep: de hulpbalk wacht op het voorstel voordat hij iets laat zien.
  const { mutateAsync: aiRewrite } = useCustomMutation();
  const { data: permissions } = usePermissions<string[]>();
  const aiEnabled = Array.isArray(permissions) && permissions.includes(AI_ROLE);
  const canEdit = Array.isArray(permissions) && permissions.includes(EDITOR_ROLE);

  const isEmail = template.type === "EMAIL";
  const isStandaard = template.origin === "STANDAARD";

  // Data-integrity guard: a fixed-layout raw-print ("db-doc") body must NOT load into TipTap (it would
  // reflow/corrupt the fixed layout). Such templates use a SAFE raw-HTML mode (read-only preview + a
  // verbatim <textarea> body editor). The discriminator mirrors Jmix isRawPrintTemplate. The mode is
  // fixed per the loaded template (a save can't flip a fragment into a full document here).
  const [overgenomenDocument, setOvergenomenDocument] = useState(false);
  const isRawPrint = useMemo(
    () => overgenomenDocument || isRawPrintBody(template.bodyHtml),
    [overgenomenDocument, template.bodyHtml],
  );

  const [subject, setSubject] = useState(template.subject ?? "");
  const [letterhead, setLetterhead] = useState<Letterhead>(() => parseLetterhead(template.letterheadJson));
  const [version, setVersion] = useState<number | null>(template.version);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [showTestMail, setShowTestMail] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // "Bekijken" in de melding rechtsonder: is het nagebouwde document voor dit sjabloon klaar, dan gaat
  // het venster hier open met het resultaat.
  const importStand = useImportTaak();
  useEffect(() => {
    if ((importStand.status === "klaar" || importStand.status === "fout") && importStand.bekijken
        && importStand.templateId === String(template.id)) {
      setShowImport(true);
      importTaak.bekeken();
    }
  }, [importStand, template.id]);
  const [paneel, setPaneel] = useState<EditorPaneel>("editor");
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // De versiegeschiedenis stond alleen in de leesweergave die er nu niet meer tussen zit. Hier pas
  // opgehaald als je het paneel opent: een sjabloon open je meestal om te schrijven, niet om terug te kijken.
  const { data: versiesData, refetch: haalVersiesOpnieuw } = useCustom<CommTemplateVersion[]>({
    url: `communicatie/templates/${template.id}/versions`,
    method: "get",
    queryOptions: { enabled: paneel === "geschiedenis" },
  });
  const versies = Array.isArray(versiesData?.data) ? versiesData.data : [];

  // Raw-print mode: the body is held verbatim in this state (never round-tripped through TipTap) and is
  // the save payload. The textarea ref lets the variable palette insert a {token} at the caret.
  const [rawBody, setRawBody] = useState<string>(template.bodyHtml ?? "");
  // Rechtermuisknop op een selectie: positie van het menu plus de HTML die toen geselecteerd was. De
  // selectie leggen we hier vast en niet pas bij het doorvoeren -- tegen die tijd heeft de gebruiker
  // in de dialoog geklikt en is de selectie in het iframe allang weg.
  const [aiSelectie, setAiSelectie] = useState<{ html: string; x: number; y: number } | null>(null);
  const [aiDialoogOpen, setAiDialoogOpen] = useState(false);
  const rawBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const rawCanvasRef = useRef<RawPrintHandle | null>(null);

  // De afdrukstylesheet van de server: dezelfde die de pdf gebruikt, zodat de editor het document
  // toont zoals het gedrukt wordt. Komt hij niet binnen, dan bewerk je het zonder opmaak — vervelend,
  // maar beter dan een editor die niet opent.
  const [printCss, setPrintCss] = useState<string | null>(null);
  useEffect(() => {
    if (!isRawPrint) return;
    let active = true;
    fetchPrintCss()
      .then((css) => { if (active) setPrintCss(css); })
      .catch(() => { if (active) setPrintCss(""); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRawPrint]);

  // In raw-print mode TipTap is never mounted; pass empty content so the (unconditional) hook is cheap.
  const editor = useEditor({
    extensions: commEditorExtensions(),
    content: isRawPrint ? "" : normalizeVariables(template.bodyHtml),
    editable: !isRawPrint,
    onUpdate: () => {
      setDirty(true);
      onChanged?.(true);
    },
  });

  /**
   * Het overgenomen document in de editor zetten. Het gaat naar de vaste-opmaakkant: de layout die
   * net is nagebouwd hoort niet door de tekstverwerker heen. Opslaan doe je zelf — dit zet het klaar
   * en markeert het als gewijzigd, zoals elke andere bewerking.
   */
  const neemDocumentOver = (bodyHtml: string) => {
    setOvergenomenDocument(true);
    setRawBody(bodyHtml);
    markDirty();
  };

  // The single source of truth for the body being saved: the raw textarea verbatim, or the WYSIWYG HTML.
  const currentBody = () => (isRawPrint ? rawBody : editor?.getHTML() ?? "");

  // Keep dirty state in sync when the subject changes too.
  const markDirty = () => {
    setDirty(true);
    onChanged?.(true);
  };

  /**
   * Een heel blok invoegen op de cursor.
   *
   * <p>Gaat langs dezelfde weg als een los veld, zodat het op de plek belandt waar je staat en niet
   * onderaan het document. Alleen in de afdrukeditor: daar is de opmaak letterlijk, en daar hoort dit
   * soort blok ook thuis.
   */
  const insertBlok = (html: string) => {
    if (isRawPrint && rawCanvasRef.current) {
      rawCanvasRef.current.insertHtml(html);
      markDirty();
    }
  };

  const insertVariable = (token: string) => {
    // Raw-print mode: splice the literal {token} at the textarea caret (the markup is edited verbatim,
    // so a real {Group.Field} token is what the render pipeline expects — no pill node).
    if (isRawPrint) {
      const literal = `{${token}}`;
      // Op de cursor in het document zelf; alleen als dat er niet is terug naar het tekstveld.
      if (rawCanvasRef.current) {
        rawCanvasRef.current.insertHtml(literal);
        markDirty();
        return;
      }
      const ta = rawBodyRef.current;
      if (ta) {
        const start = ta.selectionStart ?? rawBody.length;
        const end = ta.selectionEnd ?? rawBody.length;
        const next = rawBody.slice(0, start) + literal + rawBody.slice(end);
        setRawBody(next);
        markDirty();
        // Restore the caret just after the inserted token on the next tick.
        requestAnimationFrame(() => {
          ta.focus();
          const caret = start + literal.length;
          ta.setSelectionRange(caret, caret);
        });
      } else {
        setRawBody((prev) => prev + literal);
        markDirty();
      }
      return;
    }
    if (!editor) return;
    editor.chain().focus().insertContent({ type: "variable", attrs: { token } }).run();
  };

  // Insert a library image as a self-contained base64 data: URI (renders in editor + preview + PDF).
  // The Image node only round-trips src/alt/title; width is left to the rendered <img>/CSS (the metadata
  // defaultWidthPx is intentionally not forced as a TipTap attr it would drop on serialize).
  const insertImage = (dataUri: string, _widthPx: number | null) => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: "image", attrs: { src: dataUri } }).run();
    markDirty();
  };

  // Apply a reviewed AI proposal: swap the whole body, normalizing bare {tokens} back into pills so the
  // variable nodes survive (the same load-time round-trip the editor uses). Marks the doc dirty.
  /**
   * Een vrije vraag aan de AI: "haal de kolom aantal weg", "zet er een blok met het chipnummer onder".
   *
   * <p>Gaat naar dezelfde /rewrite als de AI-assistent, met de vraag als {@code customInstruction} en
   * het hele document erbij -- de AI moet zien waar hij iets tussen zet. Het antwoord is een
   * **voorstel**: pas als jij op toepassen drukt staat het in het document.
   *
   * <p>De harde grens: een voorstel waarin de server de {Tokens} niet ongeschonden terugvindt
   * ({@code placeholdersPreserved === false}) kan niet toegepast worden. Een weggevallen token merk je
   * namelijk niet in de editor -- je merkt het als er bij de klant een lege plek op de factuur staat.
   */
  const aiVraag = async (vraag: string): Promise<AiUitkomst> => {
    try {
      const antwoord = await aiRewrite({
        url: "communicatie/templates/ai/rewrite",
        method: "post",
        values: {
          templateId: template?.id,
          action: "custom",
          customInstruction: vraag,
          fullTemplateHtml: currentBody(),
          language: "nl",
          templateType: template?.type,
          category: template?.category,
        },
      });
      const voorstel = antwoord?.data as {
        proposalHtml?: string; summary?: string; warnings?: string[]; placeholdersPreserved?: boolean;
      } | undefined;
      if (!voorstel?.proposalHtml) {
        return { ok: false, melding: t("communicatieEditor.hulp.geenVoorstel") };
      }
      if (voorstel.placeholdersPreserved === false) {
        return { ok: false, melding: t("communicatieEditor.hulp.tokensKwijt") };
      }
      return {
        ok: true,
        samenvatting: voorstel.summary || t("communicatieEditor.hulp.voorstelKlaar"),
        waarschuwingen: voorstel.warnings ?? [],
        toepassen: () => applyAiBody(voorstel.proposalHtml as string),
      };
    } catch {
      return { ok: false, melding: t("communicatieEditor.hulp.mislukt") };
    }
  };

  const applyAiBody = (html: string) => {
    // Bij vaste opmaak gaat het voorstel rechtstreeks naar het document; door TipTap heen zou de
    // layout worden platgeslagen — precies wat de vaste-opmaakmodus voorkomt.
    if (isRawPrint) {
      setRawBody(html);
      markDirty();
      return;
    }
    if (!editor) return;
    editor.commands.setContent(normalizeVariables(html), true);
    markDirty();
  };

  const previewSrcDoc = useMemo(() => {
    const html = (isRawPrint ? rawBody : editor?.getHTML()) ?? template.bodyHtml ?? "";
    return html || `<p>${t("communicatieRead.template.body.empty")}</p>`;
  }, [editor, dirty, isRawPrint, rawBody, template.bodyHtml, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Het voorbeeld zoals het echt wordt afgedrukt of verstuurd: de server vult de huidige inhoud (ook nog niet
  // opgeslagen) met voorbeeldgegevens en maakt er dezelfde PDF van als bij een echte afdruk. Opgehaald als je
  // het paneel opent en als je op Vernieuwen klikt; niet bij elke toetsaanslag, een PDF opmaken kost tijd.
  type Voorbeeld = { soort: "PRINT" | "EMAIL"; paginas: string[]; onderwerp: string | null; html: string | null };
  const [voorbeeld, setVoorbeeld] = useState<Voorbeeld | null>(null);
  const [voorbeeldFout, setVoorbeeldFout] = useState<string | null>(null);
  const { mutate: vraagVoorbeeld, isLoading: voorbeeldBezig } = useCustomMutation();
  const haalVoorbeeld = () => {
    setVoorbeeldFout(null);
    vraagVoorbeeld(
      {
        url: `communicatie/templates/${template.id}/voorbeeld`,
        method: "post",
        values: { bodyHtml: currentBody(), subject: isEmail ? subject : template.subject },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setVoorbeeld(res.data as unknown as Voorbeeld),
        onError: (e) => {
          setVoorbeeld(null);
          setVoorbeeldFout((e as HttpError)?.message || t("communicatieEditor.preview.failed"));
        },
      },
    );
  };
  useEffect(() => {
    if (paneel === "voorbeeld") haalVoorbeeld();
  }, [paneel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Letterhead field helpers (mark dirty on every edit so the save guard + ActionBar stay in sync).
  const setLh = (patch: Partial<Letterhead>) => {
    setLetterhead((prev) => ({ ...prev, ...patch }));
    markDirty();
  };

  function reload(fresh: CommTemplateDetailData) {
    // Opslaan of publiceren maakt een nieuwe versie; stond de geschiedenis al open, dan hoort die erbij.
    if (versiesData) void haalVersiesOpnieuw();
    setSubject(fresh.subject ?? "");
    setLetterhead(parseLetterhead(fresh.letterheadJson));
    setVersion(fresh.version);
    // Raw-print mode keeps the body verbatim (never through TipTap); WYSIWYG reloads the pill round-trip.
    if (isRawPrint) {
      setRawBody(fresh.bodyHtml ?? "");
    } else {
      editor?.commands.setContent(normalizeVariables(fresh.bodyHtml), false);
    }
    setDirty(false);
    setConflict(false);
    onChanged?.(false);
    onSaved(fresh);
  }

  function handleError(err: unknown) {
    const e = err as CommHttpError;
    if (e?.statusCode === 409) {
      setConflict(true);
      setErrors([]);
      setNotice(null);
      return;
    }
    if (e?.statusCode === 422) {
      setErrors(e.validationErrors && e.validationErrors.length > 0 ? e.validationErrors : [e.message]);
      setNotice(null);
      return;
    }
    setErrors([e?.message ?? t("communicatieEditor.error.generic")]);
  }

  function doSave() {
    if (!isRawPrint && !editor) return;
    setErrors([]);
    setNotice(null);
    const values: SaveValues = {
      subject: isEmail ? subject : template.subject,
      bodyHtml: currentBody(),
      letterheadJson: serializeLetterhead(letterhead),
      version,
    };
    mutate(
      {
        url: `communicatie/templates/${template.id}`,
        method: "put",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          reload(res.data as unknown as CommTemplateDetailData);
          setNotice(t("communicatieEditor.notice.saved"));
        },
        onError: handleError,
      },
    );
  }

  function doPublish() {
    if (!isRawPrint && !editor) return;
    setErrors([]);
    setNotice(null);
    // Persist the current edits first so publish validates the latest body (the publish endpoint
    // snapshots whatever is stored). Save → on success → publish.
    const values: SaveValues = {
      subject: isEmail ? subject : template.subject,
      bodyHtml: currentBody(),
      letterheadJson: serializeLetterhead(letterhead),
      version,
    };
    mutate(
      {
        url: `communicatie/templates/${template.id}`,
        method: "put",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (saveRes) => {
          const saved = saveRes.data as unknown as CommTemplateDetailData;
          setVersion(saved.version);
          mutate(
            {
              url: `communicatie/templates/${template.id}/publish`,
              method: "post",
              values: { version: saved.version },
              successNotification: false,
              errorNotification: false,
            },
            {
              onSuccess: (pubRes) => {
                reload(pubRes.data as unknown as CommTemplateDetailData);
                setNotice(t("communicatieEditor.notice.published"));
              },
              onError: handleError,
            },
          );
        },
        onError: handleError,
      },
    );
  }

  function doRestore() {
    setErrors([]);
    setNotice(null);
    mutate(
      {
        url: `communicatie/templates/${template.id}/restore-default?version=${version ?? ""}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          reload(res.data as unknown as CommTemplateDetailData);
          setNotice(t("communicatieEditor.notice.restored"));
        },
        onError: handleError,
      },
    );
  }

  // De kopregel zegt wanneer er voor het laatst is opgeslagen. Zonder dat weet je bij een lang
  // openstaand scherm niet of je werk erin zit.
  const laatstOpgeslagen = template.modifiedDate
    ? t("communicatieEditor.header.lastSaved", { args: [formatDateTime(template.modifiedDate, locale)] })
    : null;

  const versieKolommen: Column<CommTemplateVersion>[] = [
    { header: t("communicatieRead.template.version.number"), cell: (v) => v.versionNumber },
    { header: t("communicatieRead.template.version.subject"), cell: (v) => v.subject ?? "—" },
    { header: t("communicatieRead.template.version.summary"), cell: (v) => v.changeSummary ?? "—" },
    { header: t("communicatieRead.template.version.changedBy"), cell: (v) => v.changedByUser ?? "—" },
    { header: t("communicatieRead.template.version.changedAt"), cell: (v) => formatDateTime(v.changedAt, locale) },
  ];

  const meldingen = (
    <>
      {conflict && (
        <div className="comm-editor__alert comm-editor__alert--conflict" role="alert">
          {t("communicatieEditor.error.conflict")}
        </div>
      )}
      {errors.length > 0 && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          <strong>{t("communicatieEditor.error.title")}</strong>
          <ul>
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {notice && (
        <div className="comm-editor__alert comm-editor__alert--ok" role="status">
          {notice}
        </div>
      )}
    </>
  );

  return (
    <CommEditorShell
      t={t}
      titel={t("communicatieEditor.header.title")}
      ondertitel={`${template.name} (${typeLabel(template.type, t)})`}
      statusBadge={statusLabel(template.status, t)}
      herkomstBadge={originLabel(template.origin, t)}
      laatstOpgeslagen={laatstOpgeslagen}
      paneel={paneel}
      onPaneel={setPaneel}
      onVoorbeeld={() => setPaneel("voorbeeld")}
      onPubliceren={doPublish}
      publicerenUit={!canEdit || saving || (!isRawPrint && !editor)}
      onSluiten={onCancel}
      meerActies={[
        ...(canEdit && isEmail
          ? [{ label: t("communicatieEditor.testMail.action"), onClick: () => setShowTestMail(true) }]
          : []),
        ...(canEdit ? [{ label: t("communicatieImport.actie"), onClick: () => setShowImport(true) }] : []),
        ...(canEdit && !isRawPrint
          ? [{ label: t("communicatieEditor.mediaInsert.action"), onClick: () => setShowMediaPicker(true) }]
          : []),
      ]}
      links={
        <>
          <div className="ce-card ce-card-pad">
            <div className="ce-sec-ttl">
              <span className="ce-sec-ic"><CeIcon naam="settings" /></span>
              {t("communicatieEditor.settings.title")}
            </div>
            <div className="ce-rows">
              <div className="ce-row">
                <span className="ce-row-lbl">{t("communicatieRead.template.column.type")}</span>
                <span className="ce-row-val">{typeLabel(template.type, t)}</span>
              </div>
              <div className="ce-row">
                <span className="ce-row-lbl">{t("communicatieRead.template.column.category")}</span>
                <span className="ce-row-val">{categoryLabel(template.category, t)}</span>
              </div>
              <div className="ce-row">
                <span className="ce-row-lbl">{t("communicatieEditor.settings.language")}</span>
                <span className="ce-row-val">{template.languageCode ?? "\u2014"}</span>
              </div>
              <div className="ce-row">
                <span className="ce-row-lbl">{t("communicatieRead.template.column.status")}</span>
                <span className="ce-badges">
                  <span className="ce-badge green"><CeIcon naam="check" />{statusLabel(template.status, t)}</span>
                  <span className="ce-badge grey">{originLabel(template.origin, t)}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="ce-card ce-card-pad">
            <div className="ce-sec-ttl">
              <span className="ce-sec-ic"><CeIcon naam="info" /></span>
              {t("communicatieEditor.info.title")}
            </div>
            <div className="ce-field" style={{ marginTop: "11px" }}>
              <label className="ce-lbl" htmlFor="ce-naam">{t("communicatieRead.template.column.name")}</label>
              <input id="ce-naam" className="ce-input" value={template.name} readOnly />
            </div>
            <div className="ce-field">
              <label className="ce-lbl" htmlFor="ce-code">{t("communicatieEditor.info.code")}</label>
              <input id="ce-code" className="ce-input" value={template.templateKey} readOnly />
            </div>
            <div className="ce-field">
              <label className="ce-lbl" htmlFor="ce-oms">{t("communicatieEditor.info.description")}</label>
              <textarea id="ce-oms" className="ce-textarea" value={template.description ?? ""} readOnly />
            </div>
          </div>

          {isEmail && (
            <div className="ce-card ce-card-pad">
              <div className="ce-sec-ttl">
                <span className="ce-sec-ic"><CeIcon naam="mail" /></span>
                {t("communicatieRead.template.field.subject")}
              </div>
              <div className="ce-field" style={{ marginTop: "11px" }}>
                <input
                  className="ce-input"
                  aria-label={t("communicatieRead.template.field.subject")}
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    markDirty();
                  }}
                />
              </div>
            </div>
          )}
        </>
      }
      midden={
        <>
          {meldingen}
          {isRawPrint ? (
            <RawPrintToolbar canvas={rawCanvasRef} t={t} disabled={!canEdit || saving} />
          ) : (
            editor && <EditorToolbar editor={editor} t={t} disabled={saving} />
          )}
          {aiEnabled && (
            <CommAiStrip
              getBodyHtml={currentBody}
              onApplyBody={applyAiBody}
              templateId={template.id}
              t={t}
              disabled={!canEdit || saving}
            />
          )}
          {isRawPrint && (
            <div
              className="comm-editor__raw-notice"
              role="note"
              title={t("communicatieEditor.raw.notice")}
            >
              <CeIcon naam="info" />
              {t("communicatieEditor.raw.noticeShort")}
            </div>
          )}
          <div className="ce-canvas">
            {/* De linialen: de horizontale zegt hoe breed het papier is, de verticale hoe ver je
                van de bovenrand zit — bij een afdrukdocument met vaste opmaak is dat het verschil
                tussen wel en niet op de goede plek staan. */}
            <div className="ce-ruler">
              {Array.from({ length: isRawPrint ? 21 : 19 }, (_, i) => (
                <span key={i}>{i + 1}</span>
              ))}
            </div>
            <div className="ce-vruler" aria-hidden="true">
              {Array.from({ length: 27 }, (_, i) => (
                <span key={i}>{i + 1}</span>
              ))}
            </div>
            {isRawPrint ? (
              <div className="ce-page ce-rawpage">
                {printCss == null ? (
                  <p className="fawkes-muted">{t("common.loading")}</p>
                ) : (
                  <RawPrintCanvas
                    ref={rawCanvasRef}
                    // De huidige body, niet die van het opgeslagen sjabloon: een document dat hier
                    // binnenkomt nadat de editor open is (een overgenomen brief) moet ook verschijnen.
                    bodyHtml={rawBody}
                    printCss={printCss}
                    readOnly={!canEdit || saving}
                    ariaLabel={t("communicatieEditor.raw.sourceLabel")}
                    onChange={(html) => {
                      setRawBody(html);
                      markDirty();
                    }}
                    onSelectieMenu={(positie) => {
                      const selectie = rawCanvasRef.current?.selectieHtml();
                      if (!selectie) return;
                      setAiSelectie({ html: selectie, ...positie });
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="ce-page">
                <EditorContent className="ce-docbody" editor={editor} />
              </div>
            )}
            <div className="ce-cbar">
              <span className="ce-crumb">{isRawPrint ? "db-doc" : "div > p"}</span>
            </div>
          </div>
        </>
      }
      rechts={
        <>
          <div className="ce-paneel-hint">{t("communicatieEditor.insert.hint")}</div>
          <VariablePalette onInsert={insertVariable} onInsertHtml={insertBlok} onAiVraag={aiVraag}
            t={t} disabled={saving} />
        </>
      }
      ai={
        aiEnabled ? (
          <CommAiPanel
            template={template}
            getBodyHtml={currentBody}
            onApplyBody={applyAiBody}
            onInsertToken={insertVariable}
            t={t}
            disabled={saving}
          />
        ) : null
      }
      voorbeeld={
        <>
          <div className="comm-editor__voorbeeld-kop">
            <div>
              <h3>{t("communicatieEditor.preview.title")}</h3>
              <div className="ce-simple-sub">{t("communicatieEditor.preview.hint")}</div>
            </div>
            <button type="button" className="ce-btn" onClick={haalVoorbeeld} disabled={voorbeeldBezig}>
              {t("communicatieEditor.preview.refresh")}
            </button>
          </div>
          {voorbeeldBezig && <p className="fawkes-muted" role="status">{t("communicatieEditor.preview.loading")}</p>}
          {voorbeeldFout && (
            <>
              <div className="comm-editor__alert comm-editor__alert--error" role="alert">{voorbeeldFout}</div>
              {/* Zonder voorbeeld van de server in elk geval de inhoud zelf, zoals het paneel altijd liet zien. */}
              <iframe
                className="comm-editor__preview"
                title={t("communicatieEditor.preview.title")}
                sandbox=""
                srcDoc={previewSrcDoc}
              />
            </>
          )}
          {!voorbeeldBezig && voorbeeld?.soort === "PRINT" && (
            <div className="comm-editor__paginas">
              {voorbeeld.paginas.map((src, i) => (
                <img
                  key={i}
                  className="comm-editor__pagina"
                  src={src}
                  alt={t("communicatieEditor.preview.page", { args: [String(i + 1), String(voorbeeld.paginas.length)] })}
                />
              ))}
            </div>
          )}
          {!voorbeeldBezig && voorbeeld?.soort === "EMAIL" && (
            <>
              <div className="comm-editor__preview-head">
                {t("communicatieEditor.preview.subject")}: {voorbeeld.onderwerp ?? ""}
              </div>
              <iframe
                className="comm-editor__preview"
                title={t("communicatieEditor.preview.title")}
                sandbox=""
                srcDoc={voorbeeld.html ?? ""}
              />
            </>
          )}
        </>
      }
      instellingen={
        <>
          <h3>{t("communicatieEditor.pane.settings")}</h3>
          {canEdit ? (
            <fieldset className="comm-editor__letterhead">
              <legend>{t("communicatieEditor.letterhead.title")}</legend>
              <p className="comm-editor__letterhead-hint">{t("communicatieEditor.letterhead.hint")}</p>
              <div className="comm-editor__letterhead-grid">
                <Field
                  label={t("communicatieEditor.letterhead.brand")}
                  type="text"
                  value={letterhead.brand}
                  onChange={(e) => setLh({ brand: e.target.value })}
                />
                <Field
                  label={t("communicatieEditor.letterhead.name")}
                  type="text"
                  value={letterhead.name}
                  onChange={(e) => setLh({ name: e.target.value })}
                />
              </div>
              <TextareaField
                label={t("communicatieEditor.letterhead.lines")}
                rows={4}
                value={letterhead.lines.join("\n")}
                onChange={(e) => setLh({ lines: e.target.value.split("\n") })}
              />
              <TextareaField
                label={t("communicatieEditor.letterhead.footer")}
                rows={2}
                value={letterhead.footer}
                onChange={(e) => setLh({ footer: e.target.value })}
              />
            </fieldset>
          ) : (
            <div className="ce-simple-sub">{t("communicatieEditor.pane.settingsReadOnly")}</div>
          )}
        </>
      }
      geschiedenis={
        <>
          <h3>{t("communicatieEditor.pane.history")}</h3>
          <div className="ce-simple-sub">{t("communicatieEditor.pane.historyHint")}</div>
          <IndeelbareTabel tabelKey="editor-comm-template-editor"
            columns={versieKolommen}
            rows={versies}
            getRowKey={(v) => v.id}
            empty={t("communicatieRead.template.version.empty")}
          />
        </>
      }
      voetLinks={
        <>
          <span className={errors.length > 0 ? "ce-b-item" : "ce-b-ok"}>
            <CeIcon naam="check" />
            {errors.length > 0
              ? t("communicatieEditor.validation.problems", { args: [String(errors.length)] })
              : t("communicatieEditor.validation.none")}
          </span>
        </>
      }
      voetActies={
        <>
          <button type="button" className="ce-btn" onClick={doSave} disabled={saving || (!isRawPrint && !editor)}>
            {t("communicatieEditor.action.save")}
          </button>
          {isStandaard && (
            <button type="button" className="ce-btn ce-ghost" onClick={doRestore} disabled={saving}>
              <CeIcon naam="undo" />
              {t("communicatieEditor.action.restore")}
            </button>
          )}
          <button type="button" className="ce-btn ce-danger" onClick={onCancel} disabled={saving}>
            <CeIcon naam="close" />
            {t("common.cancel")}
          </button>
        </>
      }
    >
      {/* De dialogen horen bij de editor maar staan buiten de kolommen. */}
      {canEdit && isEmail && (
        <TestMailDialog templateId={template.id} open={showTestMail} onClose={() => setShowTestMail(false)} t={t} />
      )}
      {canEdit && (
        <DocumentImportDialog
          open={showImport}
          onClose={() => setShowImport(false)}
          onOvernemen={neemDocumentOver}
          bron={{ templateId: String(template.id), templateNaam: template.name }}
          t={t}
        />
      )}
      {canEdit && !isRawPrint && (
        <MediaLibraryPicker
          open={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onInsert={insertImage}
          t={t}
        />
      )}
      {/* Het snelmenu op een selectie. Buiten het iframe getekend en op de muispositie gezet: binnen het
          iframe zou het wegvallen achter de rand van het vel. Klikken buiten het menu of Escape sluit het. */}
      {aiSelectie && !aiDialoogOpen && (
        <>
          <div className="ce-ai-menu__vanger" onMouseDown={() => setAiSelectie(null)} />
          <div
            className="ce-ai-menu"
            style={{ left: aiSelectie.x, top: aiSelectie.y }}
            role="menu"
            onKeyDown={(e) => { if (e.key === "Escape") setAiSelectie(null); }}
          >
            <button type="button" role="menuitem" onClick={() => setAiDialoogOpen(true)}>
              {t("communicatieAi.selectie.menu")}
            </button>
          </div>
        </>
      )}
      {aiSelectie && aiDialoogOpen && (
        <CommAiSelectieDialoog
          open
          template={template}
          selectieHtml={aiSelectie.html}
          onSluit={() => { setAiDialoogOpen(false); setAiSelectie(null); }}
          onDoorvoeren={(html) => {
            rawCanvasRef.current?.vervangSelectie(html);
            markDirty();
          }}
        />
      )}
    </CommEditorShell>
  );
}
